/**
 * Draft-Preview session for Platform Admin.
 *
 * When ON, every axios request adds `X-Preview-Mode: draft`, and every URL
 * built by the app carries `?preview=draft` so links from the sidebar (or
 * the Publish Center iframe) stay in preview mode. The backend gates the
 * bypass server-side — a non-admin who forges the header still receives
 * published data.
 *
 * State survives navigation via sessionStorage so opening a link with
 * `?preview=draft` implicitly turns the flag on for the whole session.
 */
import React, { createContext, useContext, useEffect, useMemo, useState } from "react";

const KEY = "vypar_preview_mode";

// Read the initial state from the URL first, then sessionStorage — arriving
// at `/dashboard?preview=draft` should flip the toggle on even for a
// freshly-opened tab so the Publish Center iframe works.
function initialPreviewMode() {
  if (typeof window === "undefined") return false;
  try {
    const url = new URL(window.location.href);
    if ((url.searchParams.get("preview") || "").toLowerCase() === "draft") {
      sessionStorage.setItem(KEY, "1");
      return true;
    }
  } catch { /* ignore */ }
  try {
    return sessionStorage.getItem(KEY) === "1";
  } catch { return false; }
}

const PreviewCtx = createContext({ previewMode: false, setPreviewMode: () => {} });

export function PreviewProvider({ children }) {
  const [previewMode, setPreviewMode] = useState(initialPreviewMode());

  useEffect(() => {
    try {
      if (previewMode) sessionStorage.setItem(KEY, "1");
      else sessionStorage.removeItem(KEY);
    } catch { /* ignore quota */ }
    // Notify the axios interceptor + anyone else who cares (Publish Center
    // iframe re-syncs via a full reload of the previewed route).
    window.dispatchEvent(new CustomEvent("vyparcrm:preview-mode-changed",
                                          { detail: { previewMode } }));
  }, [previewMode]);

  const value = useMemo(() => ({ previewMode, setPreviewMode }), [previewMode]);
  return <PreviewCtx.Provider value={value}>{children}</PreviewCtx.Provider>;
}

export function usePreview() { return useContext(PreviewCtx); }

// Global read used by the axios interceptor (which lives outside React).
export function isPreviewModeActive() {
  try { return sessionStorage.getItem(KEY) === "1"; } catch { return false; }
}
